import { functions, ordersNode, usersNode, relGlobalOrdersPaymentStatusNode } from '@/api/firebase'
import { firebaseObjectToArray, firebaseObjectWithId } from '@/utils'
import { async, GET_ASYNC, ASYNC_MUTATIONS } from '@/utils/async'
import { PAYMENT_STATUSES } from '@/utils/constants'
import { USERS, ORDERS } from 'db'

const state = {
    orders: false,
    order: false,
    cancelOrders: false,
    orderHistory: {},
    ...async()
}

const getters = {
    GET_ORDERS: (state) => firebaseObjectToArray(state.orders),
    GET_ORDER: (state) => firebaseObjectWithId(state.order),
    GET_CANCEL_ORDERS: (state) => firebaseObjectToArray(state.cancelOrders),
    GET_ORDER_HISTORY: (state) => state.orderHistory,
    GET_ASYNC
}

const mutations = {
    SET_ORDERS(state, payload) {
        state.orders = payload
    },
    SET_ORDER(state, payload) {
        state.order = Object.assign({}, { ...(state.order || null) }, payload)
    },
    SET_CANCEL_ORDERS(state, payload) {
        state.cancelOrders = payload
    },
    CLEAR_ORDERS(state) {
        state.orders = false
    },
    CLEAR_ORDER(state) {
        state.order = false
    },
    CLEAR_CANCEL_ORDERS(state) {
        state.cancelOrders = false
    },
    APPEND_ORDER_HISTORY(state, payload) {
        state.orderHistory = Object.assign(
            {},
            { ...state.orderHistory },
            {
                [payload.id]: payload
            }
        )
    },
    ...ASYNC_MUTATIONS()
}

const actions = {
    // =================================== C/U/D Operations ===================================

    /**
     * CANCEL_ORDER
     *
     *     Calls the cancelOrder cloud function to add an canceled order step on an order.
     *
     * @param payload.orderId {string}
     * @param payload.action {string}
     * @param payload.currentActionId {string}
     * @returns {Promise<void>}
     */
    async CANCEL_ORDER(_, payload) {
        await functions.httpsCallable('ordersAddOrderStep')({
            orderId: payload.orderId,
            action: payload.action,
            currentActionId: payload.currentActionId
        })
    },

    /**
     * RESUBMIT_ORDER
     *
     *     Calls the resubmit order cloud function to authorize an order that failed the first time.
     *
     * @param payload.orderId {string}
     * @returns {Promise<void>}
     */
    async RESUBMIT_ORDER(_, payload) {
        await functions.httpsCallable('ordersResubmit')({
            orderId: payload.orderId
        })
    },

    /**
     * RESTORE_ORDER
     *
     *     Calls the restore order cloud function to move an order back to the order authorization process.
     *
     * @param payload.orderId {string}
     * @returns {Promise<void>}
     */
    async RESTORE_ORDER(_, payload) {
        await functions.httpsCallable('ordersRestore')({
            orderId: payload.orderId
        })
    },

    /**
     * REFUND_ORDER
     *
     *     Calls the refundOrder cloud function to add an canceled order step on an order and refund payment.
     *
     * @param payload.orderId {string} - The id of the order to update.
     * @param payload.subtotal {string} - The amount to refund on subtotal .
     * @param payload.deliveryFee {string} - The amount to refund on delivery fee.
     * @param payload.adminFee {string} - The amount to refund on admin fee.
     * @param payload.taxSubtotal {string} - The amount to refund on tax on subtotal.
     * @param payload.taxFee {string} - The amount to refund on tax on delivery fee and admin fee.
     * @returns {Promise<void>}
     */
    async REFUND_ORDER() {
        // Todo - Define refund order process
        // await functions.httpsCallable('refundOrder')({
        //     orderId: payload.orderId,
        //     subtotal: payload.subtotal,
        //     deliveryFee: payload.deliveryFee,
        //     adminFee: payload.adminFee,
        //     taxSubtotal: payload.taxSubtotal,
        //     taxFee: payload.taxFee
        // })
    },

    // =================================== Read Operations ===================================

    FETCH_ACTION_NEEDED_ORDERS({ commit }) {
        commit('REQUEST')
        commit('CLEAR_ORDERS')
        relGlobalOrdersPaymentStatusNode.child(PAYMENT_STATUSES.ACTION_NEEDED).on(
            'value',
            async (relSnapshot) => {
                const ordersData = {}
                await Promise.all(
                    Object.keys(relSnapshot.val() || {}).map(async (orderId) => {
                        const order = (await ordersNode.child(orderId).once('value')).val()
                        if (order) {
                            const userSnapshot = await usersNode
                                .child(order[ORDERS.fields.placedBy])
                                .child(USERS.fields.email)
                                .once('value')
                            const email = userSnapshot.val()
                            ordersData[orderId] = { ...order, email }
                        } else {
                            ordersData[orderId] = { ...order, email: 'n/a' }
                        }
                    })
                )
                commit('SET_ORDERS', ordersData || false)
                commit('SUCCESS')
            },
            (err) => {
                commit('ERROR', err.message)
            }
        )
    },

    /**
     * FETCH_ORDER_BY_ID
     *
     *     Retrieve a list of addresses by user id. If empty, set false. Binds a firebase connection.
     *
     * @param payload.id {string} - The id of the store order to retrieve.
     * @returns {Void}
     */
    FETCH_ORDER_BY_ID({ commit }, payload) {
        commit('REQUEST')
        commit('CLEAR_ORDER')
        ordersNode.child(payload.id).on(
            'value',
            async function(data) {
                try {
                    const orderData = data.val()
                    if (orderData) {
                        const userSnapshot = await usersNode
                            .child(orderData[ORDERS.fields.placedBy])
                            .child(USERS.fields.email)
                            .once('value')
                        const email = userSnapshot.val()
                        commit('SET_ORDER', orderData ? { ...orderData, id: payload.id, email } : false)
                    } else {
                        commit('SET_ORDER', orderData ? { ...orderData, id: payload.id, email: 'n/a' } : false)
                    }
                    commit('SUCCESS')
                } catch (err) {
                    commit('ERROR', err.message)
                }
            },
            function(err) {
                commit('ERROR', err.message)
            }
        )
    },

    /**
     * SEARCH_ORDER_BY_ID
     *
     *     Check if an order exists with the provided id.
     *
     * @param payload.id {string} - The id of the order.
     * @returns {Array || Null}
     */
    async SEARCH_ORDER_BY_ID(_, payload) {
        const snapshot = await ordersNode.child(payload.id).once('value')
        const order = snapshot.val()
        if (order) {
            const userSnapshot = await usersNode
                .child(order[ORDERS.fields.placedBy])
                .child(USERS.fields.email)
                .once('value')
            const email = userSnapshot.val()
            return {
                ...snapshot.val(),
                id: snapshot.key,
                email
            }
        }
        return null
    },

    // =================================== Listener Operations ===================================

    /**
     * REMOVE_ORDER_REFERENCE
     *
     *     Unbind a firebase connection on a child that was set.
     *
     * @param payload.id {string} - The id of the user child.
     */
    REMOVE_ORDER_REFERENCE({ commit }, payload) {
        commit('CLEAR')
        commit('CLEAR_ORDER')
        ordersNode.child(payload.id).off()
    }
}

export { state, getters, mutations, actions }
